import enLearn from "#/integrations/tolgee/messages/en/learn.json";
import koLearn from "#/integrations/tolgee/messages/ko/learn.json";
import zhLearn from "#/integrations/tolgee/messages/zh/learn.json";
import type { SupportedLocale } from "#/lib/locale";

export type LearnSection = {
	slug: string;
	titleKey: string;
};

export type LearnEntry = {
	slug: string;
	sectionSlug: string;
	sectionTitleKey: string;
	titleKey: string;
	bodyKey: string;
	related: readonly string[];
	faqs: readonly string[];
	bodyless: boolean;
};

export const LEARN_SECTIONS: readonly LearnSection[] = [
	{ slug: "getting-started", titleKey: "learn.section.getting-started" },
	{ slug: "deposit-withdraw", titleKey: "learn.section.deposit-withdraw" },
	{ slug: "trading", titleKey: "learn.section.trading" },
	{ slug: "security", titleKey: "learn.section.security" },
	{ slug: "investing", titleKey: "learn.section.investing" },
	{ slug: "case-studies", titleKey: "learn.section.case-studies" },
] as const;

type RawEntry = {
	slug: string;
	sectionSlug: string;
	related?: readonly string[];
	faqs?: readonly string[];
	bodyless?: boolean;
};

const RAW_ENTRIES: readonly RawEntry[] = [
	// Getting Started
	{ slug: "quick-start", sectionSlug: "getting-started" },
	{ slug: "usdc", sectionSlug: "getting-started" },
	{ slug: "perps-basics", sectionSlug: "getting-started" },

	// Deposit & Withdraw
	{ slug: "deposit-guide", sectionSlug: "deposit-withdraw" },
	{
		slug: "faq-deposit",
		sectionSlug: "deposit-withdraw",
		bodyless: true,
		faqs: [
			"upbit-bithumb",
			"wrong-network",
			"usdt-error",
			"wrong-network-sent",
			"button-missing",
			"no-balance-after-deposit",
			"fee-breakdown",
		],
	},
	{ slug: "withdraw-guide", sectionSlug: "deposit-withdraw" },
	{
		slug: "faq-withdraw",
		sectionSlug: "deposit-withdraw",
		bodyless: true,
		faqs: ["how-to-withdraw", "transfer-disabled", "arrival-time"],
	},
	{ slug: "eth-gas-fee", sectionSlug: "deposit-withdraw" },
	{ slug: "fee-guide", sectionSlug: "deposit-withdraw" },

	// Trading
	{ slug: "trading-guide", sectionSlug: "trading" },
	{
		slug: "faq-trading",
		sectionSlug: "trading",
		bodyless: true,
		faqs: [
			"long-short",
			"what-is-leverage",
			"what-is-liquidation",
			"cross-vs-isolated",
			"how-to-close",
			"what-is-funding",
		],
	},
	{
		slug: "leverage-explained",
		sectionSlug: "trading",
		related: ["perpetual-futures", "margin-modes", "funding-rates"],
		faqs: [
			"high-leverage-better",
			"how-to-choose",
			"what-happens-liquidation",
			"reduce-leverage",
			"leverage-cost",
		],
	},
	{
		slug: "margin-modes",
		sectionSlug: "trading",
		related: ["perpetual-futures", "leverage-explained"],
		faqs: [
			"change-mode",
			"which-is-safer",
			"isolated-prevents-all-loss",
			"leverage-interaction",
			"collateral-currency",
		],
	},
	{
		slug: "order-types",
		sectionSlug: "trading",
		related: ["leverage-explained", "perpetual-futures"],
		faqs: [
			"market-vs-limit",
			"stop-market-vs-limit",
			"tpsl-together",
			"post-only",
			"limit-always-filled",
		],
	},
	{
		slug: "funding-rates",
		sectionSlug: "trading",
		related: ["perpetual-futures", "leverage-explained"],
		faqs: [
			"how-often",
			"is-it-a-fee",
			"affects-liquidation",
			"earn-from-funding",
			"negative-funding",
		],
	},

	// Security
	{ slug: "security-guide", sectionSlug: "security" },
	{
		slug: "faq-security",
		sectionSlug: "security",
		bodyless: true,
		faqs: ["what-is-private-key", "phishing-warning", "lost-private-key"],
	},

	// Investing
	{
		slug: "what-is-a-tenbagger",
		sectionSlug: "investing",
		related: [
			"how-to-find-tenbaggers",
			"tenbagger-case-sk-hynix",
			"perps-basics",
		],
		faqs: ["meaning", "crypto-tenbagger", "leverage-counts", "how-to-find"],
	},
	{
		slug: "how-to-find-tenbaggers",
		sectionSlug: "investing",
		related: [
			"what-is-a-tenbagger",
			"tenbagger-case-sk-hynix",
			"leverage-explained",
		],
		faqs: ["how-rare", "already-pumped", "where-to-check", "leverage-shortcut"],
	},
	{
		slug: "leverage-tenbagger-math",
		sectionSlug: "investing",
		related: [
			"what-is-a-tenbagger",
			"tenbagger-case-sk-hynix",
			"leverage-explained",
		],
		faqs: ["naive-math", "right-leverage", "liq-price", "spot-tenbagger"],
	},

	// Case Studies
	{
		slug: "tenbagger-case-sk-hynix",
		sectionSlug: "case-studies",
		related: ["what-is-a-tenbagger", "how-to-find-tenbaggers"],
		faqs: [
			"was-tenbagger",
			"max-drawdown",
			"trade-without-account",
			"next-semiconductor",
		],
	},
	{
		slug: "tenbagger-case-wti-oil",
		sectionSlug: "case-studies",
		related: [
			"tenbagger-case-sk-hynix",
			"how-to-find-tenbaggers",
			"funding-rates",
		],
		faqs: ["oil-tenbagger", "negative-oil", "index-vs-return", "cycle-now"],
	},
] as const;

const sectionTitleKeyBySlug = new Map(
	LEARN_SECTIONS.map((s) => [s.slug, s.titleKey]),
);

export const LEARN_ENTRIES: readonly LearnEntry[] = RAW_ENTRIES.map((e) => ({
	slug: e.slug,
	sectionSlug: e.sectionSlug,
	sectionTitleKey: sectionTitleKeyBySlug.get(e.sectionSlug) ?? "",
	titleKey: `learn.${e.slug}.title`,
	bodyKey: `learn.${e.slug}.body`,
	related: e.related ?? [],
	faqs: e.faqs ?? [],
	bodyless: e.bodyless ?? false,
}));

export const LEARN_SLUGS = LEARN_ENTRIES.map((e) => e.slug);

export function getLearnEntry(slug: string): LearnEntry | undefined {
	return LEARN_ENTRIES.find((e) => e.slug === slug);
}

export function isLearnSlug(value: string): boolean {
	return LEARN_SLUGS.includes(value);
}

export function getEntriesBySection(
	sectionSlug: string,
): readonly LearnEntry[] {
	return LEARN_ENTRIES.filter((e) => e.sectionSlug === sectionSlug);
}

const FALLBACK: Record<SupportedLocale, Record<string, string>> = {
	en: enLearn,
	ko: koLearn,
	zh: zhLearn,
};

const EN_FALLBACK = FALLBACK.en;

export type LearnTranslate = (key: string) => string;

export function createLearnTranslate(
	t: LearnTranslate,
	locale: SupportedLocale,
): LearnTranslate {
	const localeFallback = FALLBACK[locale] ?? EN_FALLBACK;
	return (key: string) => {
		const translated = t(key);
		if (translated === key) {
			return localeFallback[key] ?? EN_FALLBACK[key] ?? key;
		}
		return translated;
	};
}

export type LearnArticle = {
	slug: string;
	sectionSlug: string;
	sectionTitleKey: string;
	title: string;
	body: string;
	related: readonly string[];
};

export type LearnFaqItem = { question: string; answer: string };

/** Resolve an entry's FAQ slugs into question/answer pairs (answers are raw
 *  markdown). Single source for both the rendered FAQ markdown and the
 *  `FAQPage` JSON-LD in `lib/seo.ts`. */
export function buildFaqItems(
	t: LearnTranslate,
	entry: LearnEntry,
): LearnFaqItem[] {
	return entry.faqs.map((faqSlug) => ({
		question: t(`learn.${entry.slug}.${faqSlug}.title`),
		answer: t(`learn.${entry.slug}.${faqSlug}.body`),
	}));
}

function buildFaqMarkdown(t: LearnTranslate, entry: LearnEntry): string {
	const items = buildFaqItems(t, entry);
	if (items.length === 0) return "";
	return items
		.map(({ question, answer }) => `## ${question}\n\n${answer}`)
		.join("\n\n---\n\n");
}

export function buildArticle(
	t: LearnTranslate,
	slug: string,
): LearnArticle | undefined {
	const entry = getLearnEntry(slug);
	if (!entry) return undefined;

	const mainBody = entry.bodyless ? "" : t(entry.bodyKey);
	const faqBody = buildFaqMarkdown(t, entry);
	const body =
		mainBody && faqBody
			? `${mainBody}\n\n---\n\n${faqBody}`
			: mainBody || faqBody;

	return {
		slug,
		sectionSlug: entry.sectionSlug,
		sectionTitleKey: entry.sectionTitleKey,
		title: t(entry.titleKey),
		body,
		related: entry.related,
	};
}

export function listArticles(t: LearnTranslate): LearnArticle[] {
	return LEARN_ENTRIES.map((e) => buildArticle(t, e.slug)).filter(
		(a): a is LearnArticle => Boolean(a),
	);
}

export { deriveMetaDescription as deriveArticleDescription } from "#/lib/markdown-plain";
